// @greenlight-spec: 19-design-system.md
// Shared primitives for app-detail tab bodies. All tab files import from here
// rather than re-declaring local copies, keeping the loading/error/empty/stat
// vocabulary consistent across tabs.
import * as React from 'react';

import { AD_EYEBROW, AD_STAT } from '@/components/greenlight/surfaces';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/** Standard loading placeholder for a tab body. */
export function TabLoadingRows(): React.JSX.Element {
  return (
    <div className="space-y-2" data-testid="tab-loading">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

/** Standard error panel for a tab body. */
export function TabErrorPanel({ message }: { message: string }): React.JSX.Element {
  return (
    <Alert variant="destructive" data-testid="tab-error">
      <AlertTitle>Couldn't load this section</AlertTitle>
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}

/** Standard empty state for a tab body. */
export function TabInlineEmpty({ message }: { message: string }): React.JSX.Element {
  return (
    <div className="rounded-gl border border-border p-6 text-center text-sm text-muted-foreground">
      {message}
    </div>
  );
}

/**
 * Stat tile for the AD_STAT card surface.
 * Canonical weight is font-extrabold (matches AppMetricsPanel, the established source).
 * Accepts an optional className for surface overrides (e.g. tone tints in AppMetricsPanel).
 * Accepts an optional valueClassName for value text color overrides.
 */
export function AdStatTile({
  label,
  value,
  sub,
  className,
  valueClassName,
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  className?: string;
  valueClassName?: string;
}): React.JSX.Element {
  return (
    <div className={cn(AD_STAT, 'flex flex-col gap-1.5', className)}>
      <AdEyebrow>{label}</AdEyebrow>
      <p className="leading-none">
        <span
          className={cn(
            'text-[30px] font-extrabold leading-none tracking-tight tabular-nums text-foreground',
            valueClassName,
          )}
        >
          {value}
        </span>
        {sub !== undefined && <span className="ml-2 text-sm text-muted-foreground">{sub}</span>}
      </p>
    </div>
  );
}

/**
 * Small all-caps eyebrow label using the AD_EYEBROW class.
 * Replaces raw `<p className={AD_EYEBROW}>` usages across app-detail.
 * Accepts an optional className for layout overrides (e.g. mb-3 spacing).
 */
export function AdEyebrow({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  return <p className={cn(AD_EYEBROW, className)}>{children}</p>;
}

/**
 * Chip for displaying an env-var name (monospace, truncated).
 * Used in EnvVarsTab to display user and managed variable names.
 */
export function EnvNameChip({ name }: { name: string }): React.JSX.Element {
  return (
    <span className="inline-block font-mono text-[12.5px] font-semibold bg-muted px-2 py-0.5 rounded-gl">
      {name}
    </span>
  );
}
